import React, { useState, useMemo, useRef } from "react";
import { T } from "./theme";
import { SectionHeader, Card, Btn, Input, Sel, Avatar } from "./ui";
import { supabase, isSupabaseReady } from "./supabase";
import MeuPerfil from "./MeuPerfil";

// ═══════════════════════════════════════════════════════════════════════
// CONFIGURAÇÕES DO CORRETOR (hub)
// Tabs: Meu Perfil · Conta & Pagamento
// ═══════════════════════════════════════════════════════════════════════

export default function ConfigCorretor({ currentUser, corretores, times, setUser, setCorretores, targetCorretorId, embedded = false }) {
  // Admin edita outro corretor → busca pelo targetCorretorId
  // Corretor edita o próprio → busca pelo currentUser
  const meu = useMemo(() => {
    if (targetCorretorId) {
      return corretores.find(c => c.id === targetCorretorId || c._uuid === targetCorretorId);
    }
    return corretores.find(c => c._uuid === currentUser._corretorUuid || c.id === currentUser.corretorId);
  }, [corretores, currentUser, targetCorretorId]);

  const adminEditandoOutro = !!targetCorretorId;

  if (!meu) return <div style={{ padding: 40, textAlign: "center", color: T.textDim }}>Perfil não encontrado.</div>;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {!embedded && (
        <SectionHeader icon="⚙️" title="Configurações" subtitle={adminEditandoOutro ? `Editando: ${meu.nome}` : "Seu perfil, conta e dados de pagamento"} />
      )}
      {embedded && adminEditandoOutro && (
        <div style={{ fontSize: 12, color: T.textSub }}>
          Editando dados de <strong style={{ color: T.brand }}>{meu.nome}</strong>
        </div>
      )}

      {!adminEditandoOutro && (
        <MeuPerfil currentUser={currentUser} corretores={corretores} times={times} />
      )}
      <ContaPagamento
        currentUser={currentUser}
        meu={meu}
        times={times}
        setUser={setUser}
        setCorretores={setCorretores}
        adminEditandoOutro={adminEditandoOutro}
      />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// CONTA & PAGAMENTO — dados editáveis + senha
// ═══════════════════════════════════════════════════════════════════════

function ContaPagamento({ currentUser, meu, times, setUser, setCorretores, adminEditandoOutro = false }) {
  const [form, setForm] = useState({
    nome: meu?.nome || "",
    email: meu?.email || "",
    telefone: meu?.telefone || "",
    cidade: meu?.cidade || "",
    uf: meu?.uf || "",
    sexo: meu?.sexo || "F",
    cpf: meu?.cpf || "",
    timeId: meu?.timeId || "",
    chavePix: meu?.chavePix || "",
    bancoPix: meu?.bancoPix || "",
    nomePix: meu?.nomePix || "",
    cnpj: meu?.cnpj || "",
    tipoEmpresa: meu?.tipoEmpresa === "CNPJ" ? "CNPJ" : "CPF",
    tagUnica: meu?.tagUnica || "",
  });
  const [foto, setFoto] = useState(meu?.foto || meu?.foto_url || null);
  const [fotoUp, setFotoUp] = useState(false);
  const fileRef = useRef(null);
  const [senhaAtual, setSenhaAtual] = useState("");
  const [senhaNova, setSenhaNova] = useState("");
  const [senhaConfirma, setSenhaConfirma] = useState("");
  const [msg, setMsg] = useState(null);
  const [salvando, setSalvando] = useState(false);

  async function uploadFoto(file) {
    if (!isSupabaseReady || !meu?._uuid || !file) return;
    if (file.size > 5 * 1024 * 1024) { setMsg({ tipo: "err", texto: "Foto máx 5 MB" }); return; }
    if (!/image\//i.test(file.type)) { setMsg({ tipo: "err", texto: "Use uma imagem" }); return; }
    setFotoUp(true);
    setMsg(null);
    try {
      const ext = (file.name.split(".").pop() || "jpg").toLowerCase();
      const path = `corretores/${meu._uuid}/${Date.now()}.${ext}`;
      const { error: upErr } = await supabase.storage.from("avatars").upload(path, file, { contentType: file.type, upsert: true });
      if (upErr) throw upErr;
      const { data: pub } = supabase.storage.from("avatars").getPublicUrl(path);
      const url = pub.publicUrl;
      const { error: updErr } = await supabase.from("corretores").update({ foto_url: url }).eq("id", meu._uuid);
      if (updErr) throw updErr;
      setFoto(url);
      meu.foto = url;
      meu.foto_url = url;
      setMsg({ tipo: "ok", texto: "✅ Foto atualizada" });
    } catch (e) {
      setMsg({ tipo: "err", texto: "Erro: " + e.message });
    } finally { setFotoUp(false); }
  }

  const ufs = ["AC","AL","AM","AP","BA","CE","DF","ES","GO","MA","MG","MS","MT","PA","PB","PE","PI","PR","RJ","RN","RO","RR","RS","SC","SE","SP","TO"];

  async function salvarDados() {
    if (!isSupabaseReady || !meu?._uuid) return;
    setSalvando(true);
    try {
      // Mapear timeId frontend → time_id UUID Supabase
      const timeUuid = form.timeId && times ? (times.find(t => t.id === form.timeId)?._uuid || null) : null;
      const nz = s => (s && s.trim()) ? s.trim() : null;  // null pra campos vazios (evita unique constraint em "")
      const { error } = await supabase.from("corretores").update({
        nome: form.nome.trim(),
        email: nz(form.email),
        telefone: nz(form.telefone),
        cidade: nz(form.cidade),
        uf: form.uf || null,
        cpf: nz(form.cpf),
        sexo: form.sexo || "F",
        time_id: timeUuid,
        chave_pix: nz(form.chavePix),
        banco_pix: nz(form.bancoPix),
        nome_pix: nz(form.nomePix),
        cnpj: nz(form.cnpj),
        tipo_empresa: form.tipoEmpresa,
        tag_unica: form.tagUnica.trim().toLowerCase() || null,
      }).eq("id", meu._uuid);
      if (error) throw error;
      // update local
      if (setCorretores) {
        setCorretores(prev => prev.map(c => (c._uuid === meu._uuid || c.id === meu.id)
          ? { ...c, ...form, _uuid: meu._uuid, id: meu.id, timeId: form.timeId }
          : c));
      }
      setMsg({ tipo: "ok", texto: "✅ Dados atualizados!" });
    } catch (e) {
      setMsg({ tipo: "err", texto: "Erro: " + e.message });
    }
    setSalvando(false);
  }

  async function alterarSenha() {
    if (!senhaNova || senhaNova.length < 6) { setMsg({ tipo: "err", texto: "Senha deve ter no mínimo 6 caracteres." }); return; }
    if (!adminEditandoOutro && senhaNova !== senhaConfirma) { setMsg({ tipo: "err", texto: "Senhas não coincidem." }); return; }
    if (!isSupabaseReady) return;
    setSalvando(true);
    try {
      // Define alvo: admin edita outro → usuario do corretor target | senão → currentUser
      const filtro = adminEditandoOutro
        ? { corretor_id: meu._uuid }
        : { username: currentUser.username };
      const colChave = Object.keys(filtro)[0];
      const { error } = await supabase.from("usuarios").update({ password: senhaNova }).eq(colChave, filtro[colChave]);
      if (error) throw error;
      setMsg({ tipo: "ok", texto: "✅ Senha alterada!" });
      setSenhaAtual(""); setSenhaNova(""); setSenhaConfirma("");
    } catch (e) {
      setMsg({ tipo: "err", texto: "Erro: " + e.message });
    }
    setSalvando(false);
  }

  async function excluirCorretor() {
    if (!adminEditandoOutro || !isSupabaseReady) return;
    if (!window.confirm(`Excluir DEFINITIVAMENTE ${meu.nome}? Esta ação não pode ser desfeita.`)) return;
    if (!window.confirm(`Tem certeza? Digite "EXCLUIR" no próximo prompt.`)) return;
    const conf = window.prompt('Para confirmar, digite EXCLUIR:');
    if (conf !== "EXCLUIR") { setMsg({ tipo: "err", texto: "Cancelado." }); return; }
    setSalvando(true);
    try {
      // Desliga usuario
      await supabase.from("usuarios").update({ corretor_id: null }).eq("corretor_id", meu._uuid);
      // Reaponta filhos pra null (viram raiz)
      await supabase.from("corretores").update({ indicado_por: null }).eq("indicado_por", meu._uuid);
      // Deleta corretor
      const { error } = await supabase.from("corretores").delete().eq("id", meu._uuid);
      if (error) throw error;
      if (setCorretores) setCorretores(prev => prev.filter(c => c._uuid !== meu._uuid && c.id !== meu.id));
      setMsg({ tipo: "ok", texto: "✅ Corretor excluído. Recarregue a página." });
    } catch (e) {
      setMsg({ tipo: "err", texto: "Erro: " + e.message });
    }
    setSalvando(false);
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {msg && (
        <div style={{
          padding: "10px 14px", borderRadius: T.r.md,
          background: msg.tipo === "ok" ? T.ok + "15" : T.err + "15",
          border: `1px solid ${msg.tipo === "ok" ? T.ok : T.err}44`,
          color: msg.tipo === "ok" ? T.ok : T.err, fontSize: 12,
        }}>{msg.texto}</div>
      )}

      <Card>
        <div style={{ fontSize: 11, color: T.textSub, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700, marginBottom: 16 }}>
          👤 Dados Pessoais
        </div>

        {/* Foto: admin pode trocar foto do corretor; corretor vê foto no card de cima */}
        {adminEditandoOutro && (
          <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 18 }}>
            <div style={{ position: "relative" }}>
              <Avatar
                initials={(meu.nome || "?").split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase()}
                size={72}
                photo={foto}
              />
              <button
                onClick={() => fileRef.current?.click()}
                disabled={fotoUp}
                title="Trocar foto"
                style={{
                  position: "absolute", bottom: -2, right: -2,
                  width: 28, height: 28, borderRadius: "50%",
                  background: T.brand, color: T.textInv,
                  border: `2px solid ${T.surface}`, cursor: "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 14, fontFamily: "inherit",
                }}
              >{fotoUp ? "…" : "📷"}</button>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={e => uploadFoto(e.target.files?.[0])}
              />
            </div>
            <div style={{ fontSize: 11, color: T.textDim }}>
              JPG/PNG/WebP · máx 5 MB
            </div>
          </div>
        )}

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div style={{ gridColumn: "1 / -1" }}>
            <Input label="Nome e sobrenome" value={form.nome} onChange={e => setForm({ ...form, nome: e.target.value })} />
          </div>
          <Input label="E-mail" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
          <Input label="WhatsApp" value={form.telefone} onChange={e => setForm({ ...form, telefone: e.target.value })} placeholder="(85) 99999-9999" />
          <Input label="CPF" value={form.cpf} onChange={e => setForm({ ...form, cpf: e.target.value })} placeholder="000.000.000-00" />
          <Sel label="Tratamento" value={form.sexo} onChange={e => setForm({ ...form, sexo: e.target.value })}>
            <option value="F">Corretora (feminino)</option>
            <option value="M">Corretor (masculino)</option>
            <option value="O">Outro</option>
            <option value="N">Prefiro não dizer</option>
          </Sel>
          <Input label="Nome de usuário (@)" value={form.tagUnica} onChange={e => setForm({ ...form, tagUnica: e.target.value })} placeholder="ex: cauanp" />
          <Input label="Cidade" value={form.cidade} onChange={e => setForm({ ...form, cidade: e.target.value })} />
          <Sel label="Estado" value={form.uf} onChange={e => setForm({ ...form, uf: e.target.value })}>
            {ufs.map(u => <option key={u} value={u}>{u}</option>)}
          </Sel>
          {adminEditandoOutro && (
            <div style={{ gridColumn: "1 / -1" }}>
              <Sel label="Time regional" value={form.timeId} onChange={e => setForm({ ...form, timeId: e.target.value })}>
                <option value="">— Sem time —</option>
                {(times || [])
                  .filter(t => t.ativo !== false)
                  .sort((a, b) => (a.nome || "").localeCompare(b.nome || ""))
                  .map(t => (
                    <option key={t.id} value={t.id}>{t.emoji} {t.nome} · {t.cidade}-{t.uf}</option>
                  ))}
              </Sel>
            </div>
          )}
        </div>
      </Card>

      <Card>
        <div style={{ fontSize: 11, color: T.textSub, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700, marginBottom: 16 }}>
          💳 Dados Bancários e Fiscais
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div style={{ gridColumn: "1 / -1" }}>
            <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6 }}>
              <div style={{ fontSize: 10, color: T.textDim, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600 }}>Documento</div>
              <div style={{ display: "flex", gap: 4 }}>
                {["CPF", "CNPJ"].map(tipo => (
                  <button
                    key={tipo}
                    type="button"
                    onClick={() => setForm({ ...form, tipoEmpresa: tipo })}
                    style={{
                      padding: "4px 12px", borderRadius: 100,
                      background: form.tipoEmpresa === tipo ? T.brand : "transparent",
                      color: form.tipoEmpresa === tipo ? T.textInv : T.textSub,
                      border: `1px solid ${form.tipoEmpresa === tipo ? T.brand : T.borderMid}`,
                      cursor: "pointer", fontSize: 10, fontWeight: 700, fontFamily: "inherit",
                    }}
                  >{tipo}</button>
                ))}
              </div>
            </div>
            <Input
              value={form.cnpj}
              onChange={e => setForm({ ...form, cnpj: e.target.value })}
              placeholder={form.tipoEmpresa === "CNPJ" ? "00.000.000/0000-00" : "000.000.000-00"}
            />
          </div>
          <Input label="Titular da conta" value={form.nomePix} onChange={e => setForm({ ...form, nomePix: e.target.value })} placeholder="Nome que aparece na conta" />
          <Input label="Banco" value={form.bancoPix} onChange={e => setForm({ ...form, bancoPix: e.target.value })} placeholder="Ex: Nubank, Bradesco" />
          <div style={{ gridColumn: "1 / -1" }}>
            <Input label="Chave Pix" value={form.chavePix} onChange={e => setForm({ ...form, chavePix: e.target.value })} placeholder="CPF, e-mail, telefone ou chave aleatória" />
          </div>
        </div>
      </Card>

      <Btn onClick={salvarDados} disabled={salvando} style={{ justifyContent: "center", padding: 14 }}>
        {salvando ? "Salvando..." : "💾 Salvar alterações"}
      </Btn>

      <Card>
        <div style={{ fontSize: 11, color: T.textSub, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700, marginBottom: 16 }}>
          🔒 {adminEditandoOutro ? "Definir nova senha do corretor" : "Alterar Senha"}
        </div>
        {adminEditandoOutro ? (
          <>
            <div style={{ fontSize: 11, color: T.textDim, marginBottom: 10 }}>
              Você está alterando a senha de <strong>{meu.nome}</strong>. Comunique a nova senha pra ele(a).
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 12 }}>
              <Input label="Nova senha (mín 6)" type="password" value={senhaNova} onChange={e => setSenhaNova(e.target.value)} />
            </div>
          </>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
            <Input label="Senha atual" type="password" value={senhaAtual} onChange={e => setSenhaAtual(e.target.value)} />
            <Input label="Nova senha" type="password" value={senhaNova} onChange={e => setSenhaNova(e.target.value)} />
            <Input label="Confirmar nova" type="password" value={senhaConfirma} onChange={e => setSenhaConfirma(e.target.value)} />
          </div>
        )}
        <div style={{ marginTop: 12 }}>
          <Btn variant="ghost" onClick={alterarSenha} disabled={salvando} style={{ fontSize: 12 }}>
            🔒 {adminEditandoOutro ? "Definir senha" : "Alterar senha"}
          </Btn>
        </div>
      </Card>

      {adminEditandoOutro && (
        <Card style={{ borderColor: T.err + "44" }}>
          <div style={{ fontSize: 11, color: T.err, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700, marginBottom: 8 }}>
            ⚠️ Zona de perigo
          </div>
          <div style={{ fontSize: 12, color: T.textSub, marginBottom: 12 }}>
            Excluir o cadastro de <strong>{meu.nome}</strong>. Filhos diretos viram raiz, usuário do sistema é desvinculado.
          </div>
          <Btn variant="danger" onClick={excluirCorretor} disabled={salvando} style={{ fontSize: 12 }}>
            🗑️ Excluir corretor
          </Btn>
        </Card>
      )}
    </div>
  );
}
